import * as _ from 'underscore';

import { Messages } from '../../codegen/Localize';
import { imgPath } from '../../common/utilities/ImgUrl';
import { AuthorizableType } from './AuthorizableType';

export enum Capabilities {
  addComment = 'add_comment',
  changeHierarchy = 'change_hierarchy',
  changePermissions = 'change_permissions',
  connect = 'connect',
  deleteItem = 'delete',
  editPublishedConnection = 'edit_published_connection',
  execute = 'execute',
  exportData = 'export_data',
  exportImage = 'export_image',
  exportXml = 'export_xml',
  filter = 'filter',
  inheritedProjectLeader = 'inherited_project_leader', // This is a read-only capability, not shown in the permissions data grid
  projectLeader = 'project_leader',
  read = 'read',
  shareView = 'share_view',
  viewComments = 'view_comments',
  vud = 'vud',
  webAuthoring = 'web_authoring',
  webAuthoringForFlows = 'web_authoring_for_flows',
  write = 'write',
}

export interface ICapabilityInfo {
  icon: string;
  disabledIcon: string;
  message: () => string;
  name: string;
}

export const CapabilityInfo: _.Dictionary<ICapabilityInfo> = {
  addComment: {
    message: Messages.capability_add_comment,
    icon: imgPath('permission_capability_add_comment.svg'),
    disabledIcon: imgPath('permission_capability_add_comment_disabled.svg'),
    name: Capabilities.addComment
  },
  changeHierarchy: {
    message: Messages.capability_change_hierarchy,
    icon: imgPath('permission_capability_change_hierarchy.svg'),
    disabledIcon: imgPath('permission_capability_change_hierarchy_disabled.svg'),
    name: Capabilities.changeHierarchy
  },
  changePermissions: {
    message: Messages.capability_change_permissions,
    icon: imgPath('permission_capability_change_permissions.svg'),
    disabledIcon: imgPath('permission_capability_change_permissions_disabled.svg'),
    name: Capabilities.changePermissions
  },
  connect: {
    message: Messages.capability_connect,
    icon: imgPath('permission_capability_connect.svg'),
    disabledIcon: imgPath('permission_capability_connect_disabled.svg'),
    name: Capabilities.connect
  },
  delete: {
    message: Messages.capability_delete,
    icon: imgPath('permission_capability_delete.svg'),
    disabledIcon: imgPath('permission_capability_delete_disabled.svg'),
    name: Capabilities.deleteItem
  },
  editPublishedConnection: {
    message: Messages.capability_write_content,
    icon: imgPath('permission_capability_write.svg'),
    disabledIcon: imgPath('permission_capability_write_disabled.svg'),
    name: Capabilities.editPublishedConnection
  },
  executeFlow: {
    message: Messages.capability_execute_flow,
    icon: imgPath('permission_capability_execute.svg'),
    disabledIcon: imgPath('permission_capability_execute_disabled.svg'),
    name: Capabilities.execute
  },
  exportData: {
    message: Messages.capability_export_data,
    icon: imgPath('permission_capability_export_data.svg'),
    disabledIcon: imgPath('permission_capability_export_data_disabled.svg'),
    name: Capabilities.exportData
  },
  exportImage: {
    message: Messages.capability_export_image,
    icon: imgPath('permission_capability_export_image.svg'),
    disabledIcon: imgPath('permission_capability_export_image_disabled.svg'),
    name: Capabilities.exportImage
  },
  exportXmlDatasource: {
    message: Messages.capability_export_xml_datasource,
    icon: imgPath('permission_capability_export_xml_datasource.svg'),
    disabledIcon: imgPath('permission_capability_export_xml_datasource_disabled.svg'),
    name: Capabilities.exportXml
  },
  exportXmlFlow: {
    message: Messages.capability_export_xml_flow,
    icon: imgPath('permission_capability_export_xml_flow.svg'),
    disabledIcon: imgPath('permission_capability_export_xml_flow_disabled.svg'),
    name: Capabilities.exportXml
  },
  exportXmlWorkbook: {
    message: Messages.capability_export_xml_workbook_save_copy,
    icon: imgPath('permission_capability_export_xml_workbook.svg'),
    disabledIcon: imgPath('permission_capability_export_xml_workbook_disabled.svg'),
    name: Capabilities.exportXml
  },
  filter: {
    message: Messages.capability_filter,
    icon: imgPath('permission_capability_filter.svg'),
    disabledIcon: imgPath('permission_capability_filter_disabled.svg'),
    name: Capabilities.filter
  },
  projectLeader: {
    message: Messages.capability_project_leader,
    icon: imgPath('permission_capability_project_leader.svg'),
    disabledIcon: imgPath('permission_capability_project_leader_disabled.svg'),
    name: Capabilities.projectLeader
  },
  read: {
    message: Messages.capability_read,
    icon: imgPath('permission_capability_read.svg'),
    disabledIcon: imgPath('permission_capability_read_disabled.svg'),
    name: Capabilities.read
  },
  shareView: {
    message: Messages.capability_share_view,
    icon: imgPath('permission_capability_share_view.svg'),
    disabledIcon: imgPath('permission_capability_share_view_disabled.svg'),
    name: Capabilities.shareView
  },
  viewComments: {
    message: Messages.capability_view_comments,
    icon: imgPath('permission_capability_view_comments.svg'),
    disabledIcon: imgPath('permission_capability_view_comments_disabled.svg'),
    name: Capabilities.viewComments
  },
  vud: {
    message: Messages.capability_vud,
    icon: imgPath('permission_capability_vud.svg'),
    disabledIcon: imgPath('permission_capability_vud_disabled.svg'),
    name: Capabilities.vud
  },
  webAuthoring: {
    message: Messages.capability_web_authoring,
    icon: imgPath('permission_capability_web_authoring.svg'),
    disabledIcon: imgPath('permission_capability_web_authoring_disabled.svg'),
    name: Capabilities.webAuthoring
  },
  webAuthoringForFlows: {
    message: Messages.capability_web_authoring_for_flows,
    icon: imgPath('permission_capability_web_authoring_for_flows.svg'),
    disabledIcon: imgPath('permission_capability_web_authoring_for_flows_disabled.svg'),
    name: Capabilities.webAuthoringForFlows
  },
  write: {
    message: Messages.capability_write_content,
    icon: imgPath('permission_capability_write.svg'),
    disabledIcon: imgPath('permission_capability_write_disabled.svg'),
    name: Capabilities.write
  },
  writeProject: {
    message: Messages.capability_write_project,
    icon: imgPath('permission_capability_write.svg'),
    disabledIcon: imgPath('permission_capability_write_disabled.svg'),
    name: Capabilities.write
  },
};

const CatalogCapabilities = [
  Capabilities.changePermissions,
  Capabilities.read,
  Capabilities.write
];

const DataRoleCapabilities = [
  Capabilities.changeHierarchy,
  Capabilities.changePermissions,
  Capabilities.deleteItem,
  Capabilities.read,
  Capabilities.write
];

const DatasourceCapabilities = [
  Capabilities.changeHierarchy,
  Capabilities.changePermissions,
  Capabilities.connect,
  Capabilities.deleteItem,
  Capabilities.exportXml,
  Capabilities.read,
  Capabilities.write
];

const FlowCapabilities = [
  Capabilities.changeHierarchy,
  Capabilities.changePermissions,
  Capabilities.deleteItem,
  Capabilities.execute,
  Capabilities.exportXml,
  Capabilities.read,
  Capabilities.webAuthoringForFlows,
  Capabilities.write
];

const MetricCapabilities = [
  Capabilities.changeHierarchy,
  Capabilities.changePermissions,
  Capabilities.deleteItem,
  Capabilities.read,
  Capabilities.write,
];

const PublishedConnectionCapabilities = [
  Capabilities.changeHierarchy,
  Capabilities.changePermissions,
  Capabilities.connect,
  Capabilities.deleteItem,
  Capabilities.editPublishedConnection,
  Capabilities.read,
];

const ProjectCapabilities = [
  Capabilities.read,
  Capabilities.write
];

const ViewCapabilities = [
  Capabilities.addComment,
  Capabilities.changePermissions,
  Capabilities.deleteItem,
  Capabilities.exportData,
  Capabilities.exportImage,
  Capabilities.filter,
  Capabilities.read,
  Capabilities.shareView,
  Capabilities.viewComments,
  Capabilities.vud,
  Capabilities.webAuthoring
];

const WorkbookCapabilities = [
  Capabilities.addComment,
  Capabilities.changeHierarchy,
  Capabilities.changePermissions,
  Capabilities.deleteItem,
  Capabilities.exportData,
  Capabilities.exportImage,
  Capabilities.exportXml,
  Capabilities.filter,
  Capabilities.read,
  Capabilities.shareView,
  Capabilities.viewComments,
  Capabilities.vud,
  Capabilities.webAuthoring,
  Capabilities.write
];

export const CapabilitiesByAuthorizableType: _.Dictionary<Capabilities[]> = {
  [AuthorizableType.DATABASE]: CatalogCapabilities,
  [AuthorizableType.DATABASE_TABLE_TEMPLATE]: CatalogCapabilities,
  [AuthorizableType.DATAROLE]: DataRoleCapabilities,
  [AuthorizableType.DATASOURCE]: DatasourceCapabilities,
  [AuthorizableType.FLOW]: FlowCapabilities,
  [AuthorizableType.METRIC]: MetricCapabilities,
  [AuthorizableType.PUBLISHED_CONNECTION]: PublishedConnectionCapabilities,
  [AuthorizableType.PROJECT]: ProjectCapabilities,
  [AuthorizableType.PROJECT_DATASOURCE_TEMPLATE]: DatasourceCapabilities,
  [AuthorizableType.PROJECT_DATA_ROLE_TEMPLATE]: DataRoleCapabilities,
  [AuthorizableType.PROJECT_FLOW_TEMPLATE]: FlowCapabilities,
  [AuthorizableType.PROJECT_METRIC_TEMPLATE]: MetricCapabilities,
  [AuthorizableType.PROJECT_PUBLISHED_CONNECTION_TEMPLATE]: PublishedConnectionCapabilities,
  [AuthorizableType.PROJECT_WORKBOOK_TEMPLATE]: WorkbookCapabilities,
  [AuthorizableType.TABLE]: CatalogCapabilities,
  [AuthorizableType.VIEW]: ViewCapabilities,
  [AuthorizableType.WORKBOOK]: WorkbookCapabilities,
};
